import { useCallback, useEffect, useRef, useState } from 'react'
import { Select, App } from 'antd'
import { dataSourcesApi, unwrapFilterFieldOptions, type DataSourceFilterField } from '../../services/api'

type Props = {
  field: DataSourceFilterField
  value: string[]
  onChange: (v: string[]) => void
  placeholder?: string
  /** 固定选项（如来源仅财务系统/外部导入），不走接口拉全量 */
  fixedOptions?: string[]
}

function mergeOptions(
  fetched: { value: string; label: string }[],
  selected: string[]
): { value: string; label: string }[] {
  const byVal = new Map<string, { value: string; label: string }>()
  for (const o of fetched) byVal.set(o.value, o)
  for (const v of selected) {
    if (!byVal.has(v)) byVal.set(v, { value: v, label: v })
  }
  return Array.from(byVal.values()).sort((a, b) => a.label.localeCompare(b.label, 'zh-CN'))
}

function parseFilterOptionsResponse(res: unknown): string[] {
  const root = res as Record<string, unknown> | null
  if (root && root.success === false) {
    throw new Error(String(root.message || '加载筛选选项失败'))
  }
  const items = unwrapFilterFieldOptions(res)
  if (items.length === 0 && root && root.success === true) {
    return []
  }
  if (items.length === 0 && root && root.success !== true && root.success !== undefined) {
    throw new Error('加载筛选选项失败')
  }
  return items
}

export default function FilterMultiSelect({ field, value, onChange, placeholder = '全部', fixedOptions }: Props) {
  const { message } = App.useApp()
  const [options, setOptions] = useState<{ value: string; label: string }[]>(() =>
    value.map((v) => ({ value: v, label: v }))
  )
  const [loading, setLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const selectedRef = useRef(value)
  selectedRef.current = value
  const warnedRef = useRef(false)

  const showLoadError = useCallback(
    (err: unknown) => {
      setLoadFailed(true)
      if (!warnedRef.current) {
        warnedRef.current = true
        const detail =
          err && typeof err === 'object' && 'friendlyMessage' in err
            ? String((err as { friendlyMessage?: string }).friendlyMessage)
            : err instanceof Error
              ? err.message
              : '请确认后端已更新并重启'
        message.error(`筛选选项加载失败：${detail}`)
      }
    },
    [message]
  )

  /** 无输入：不传 keyword，后端返回该字段全部去重值；fixedOptions 时本地过滤 */
  const fetchAllOptions = useCallback(async () => {
    setLoading(true)
    try {
      if (fixedOptions?.length) {
        const fetched = fixedOptions.map((v) => ({ value: v, label: v }))
        setOptions(mergeOptions(fetched, selectedRef.current))
        setLoadFailed(false)
        warnedRef.current = false
        return
      }
      const res = await dataSourcesApi.filterFieldOptions({ field })
      const items = parseFilterOptionsResponse(res)
      const fetched = items.map((v) => ({ value: v, label: v }))
      setOptions(mergeOptions(fetched, selectedRef.current))
      setLoadFailed(false)
      warnedRef.current = false
    } catch (err) {
      showLoadError(err)
      setOptions(selectedRef.current.map((v) => ({ value: v, label: v })))
    } finally {
      setLoading(false)
    }
  }, [field, fixedOptions, showLoadError])

  const fetchSearchOptions = useCallback(
    async (keyword: string) => {
      setLoading(true)
      try {
        if (fixedOptions?.length) {
          const pat = keyword.trim().toLowerCase()
          const items = fixedOptions.filter((v) => v.toLowerCase().includes(pat))
          const fetched = items.map((v) => ({ value: v, label: v }))
          setOptions(mergeOptions(fetched, selectedRef.current))
          setLoadFailed(false)
          return
        }
        const res = await dataSourcesApi.filterFieldOptions({
          field,
          keyword,
          limit: 500,
        })
        const items = parseFilterOptionsResponse(res)
        const fetched = items.map((v) => ({ value: v, label: v }))
        setOptions(mergeOptions(fetched, selectedRef.current))
        setLoadFailed(false)
      } catch (err) {
        showLoadError(err)
        setOptions(selectedRef.current.map((v) => ({ value: v, label: v })))
      } finally {
        setLoading(false)
      }
    },
    [field, fixedOptions, showLoadError]
  )

  useEffect(() => {
    void fetchAllOptions()
  }, [fetchAllOptions])

  useEffect(() => {
    setOptions((prev) => mergeOptions(prev, value))
  }, [value])

  const handleSearch = (kw: string) => {
    if (timerRef.current) clearTimeout(timerRef.current)
    const trimmed = kw.trim()
    if (!trimmed) {
      void fetchAllOptions()
      return
    }
    timerRef.current = setTimeout(() => {
      void fetchSearchOptions(trimmed)
    }, 300)
  }

  const handleOpenChange = (open: boolean) => {
    if (open) void fetchAllOptions()
  }

  return (
    <Select
      mode="multiple"
      allowClear
      showSearch
      filterOption={false}
      style={{ width: '100%' }}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onSearch={handleSearch}
      onOpenChange={handleOpenChange}
      options={options}
      loading={loading}
      maxTagCount="responsive"
      notFoundContent={loading ? '加载中…' : loadFailed ? '加载失败，请重启后端后重试' : '暂无选项'}
    />
  )
}
